/**
 * TouchUpTab — V2 Touch Up options view for the AI Workspace.
 * Shows a model dropdown, prompt textarea, Touch Up button at the top,
 * and a ShotGrid below for target shot selection.
 */

import { useState, useCallback, useMemo, useRef } from 'react';
import { AnimatePresence } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { ShotGrid } from './ShotGrid.tsx';
import { WorkspaceShotPreview } from './WorkspaceShotPreview.tsx';
import { ImageFlyOverlay } from '@/components/ImageFlyOverlay.tsx';
import { DropdownPicker } from '@/components/DropdownPicker.tsx';
import { SparkleIcon } from '@/components/icons/SparkleIcon.tsx';
import { useProjectStore } from '@/store/projectStore.ts';
import { useCommentStore } from '@/store/commentStore.ts';
import { useMobileLayout } from '@/hooks/useMobileLayout.ts';
import { useAccountStore } from '@/store/accountStore.ts';
import { canAfford } from '@/ai/tokenCosts.ts';
import { capabilities } from '@/capabilities.ts';
import { interceptEmbeddedSignIn } from './embeddedAuthGate.ts';
import { trackEvent } from '@/telemetry/trackEvent';
import { formatTimeEstimate } from '@/components/CostBanner.tsx';
import type { TimelineShot } from '@/types/timeline.ts';
import type { CatalogModel, ModelCategory } from '@/types/ai.ts';
import styles from './AiWorkspace.module.css';

const TIER_LABEL_KEYS = ['tierLabelFast', 'tierLabelQuality', 'tierLabelBest'] as const;

// (selectedIds computed per-render below — highlights the active touch-up target)

interface Props {
  // Touch Up target
  effectiveTouchUpShot: TimelineShot | null;
  setTouchUpTargetShotId: (shotId: number | null) => void;

  // Prompt
  touchUpPrompt: string;
  setTouchUpPrompt: (prompt: string) => void;

  // Model
  aiSelectedTouchUpModel: string;
  catalog: CatalogModel[];
  setSelectedModel: (category: ModelCategory, modelId: string) => void;

  // Actions
  handleTouchUpGenerate: () => void;
  canRevert: boolean;
  handleRevert: () => void;
  closeWorkspace: () => void;
}

export function TouchUpTab({
  effectiveTouchUpShot,
  setTouchUpTargetShotId,
  touchUpPrompt,
  setTouchUpPrompt,
  aiSelectedTouchUpModel,
  catalog,
  setSelectedModel,
  handleTouchUpGenerate,
  canRevert,
  handleRevert,
  closeWorkspace,
}: Props) {
  const { t } = useTranslation('aiWorkspace');
  const isMobile = useMobileLayout();

  // Dropdown state
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const toggleModelDropdown = useCallback(() => setModelDropdownOpen(v => !v), []);

  // Fullscreen preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewShotId, setPreviewShotId] = useState<number | null>(null);
  const previewSourceEl = useRef<HTMLElement | null>(null);
  const mainRef = useRef<HTMLDivElement>(null);

  // Project data for the grid
  const project = useProjectStore(s => s.project);
  const allScenes = project?.scenes ?? [];
  const separator = project?.documentSettings?.separator ?? '';
  const videoShots = useProjectStore(s => s.videoShots);
  const allShots = useMemo(
    () => allScenes.flatMap(s => s.shots),
    [allScenes],
  );
  const videoShotIds = useMemo(
    () => new Set(videoShots.map(v => v.shotId)),
    [videoShots],
  );
  const approvals = useCommentStore(s => s.approvals);
  const approvedShotIds = useMemo(
    () => new Set(approvals.filter(a => a.status === 'approved').map(a => a.shotId)),
    [approvals],
  );

  // Account state for auth / balance checks
  const accountUser = useAccountStore(s => s.accountUser);
  const accountBalance = useAccountStore(s => s.accountBalance);
  const setAuthDialogOpen = useAccountStore(s => s.setAuthDialogOpen);
  const setTokenPurchaseDialogOpen = useAccountStore(s => s.setTokenPurchaseDialogOpen);
  const standaloneAuthMode = useAccountStore(s => s.standaloneAuthMode);

  // Touch Up model list
  const models = catalog
    .filter(m => m.category === 'touchup')
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const selectedModel = models.find(m => m.id === aiSelectedTouchUpModel) ?? models[0];
  const selectedTier = selectedModel?.name ?? aiSelectedTouchUpModel;
  const perShotCost = selectedModel?.cost ?? 8;
  const perShotSeconds = selectedModel?.estSeconds ?? 15;
  const maxSeconds = Math.max(...models.map(m => m.estSeconds), 1);

  const needsAuth = !accountUser;
  const insufficient = accountBalance && !accountBalance.isBeta && !canAfford(accountBalance, perShotCost);

  // Highlight the selected touch-up target in the grid
  const selectedIds = useMemo(
    () => effectiveTouchUpShot ? new Set([effectiveTouchUpShot.id]) : new Set<number>(),
    [effectiveTouchUpShot],
  );

  // Video shot guard
  const isVideoShot = !!(effectiveTouchUpShot && videoShots.find(v => v.shotId === effectiveTouchUpShot.id));

  // Disabled conditions: no shot, no thumbnail, no prompt, or is video shot
  const generateDisabled = !effectiveTouchUpShot
    || !effectiveTouchUpShot.thumbnailDataUrl
    || !touchUpPrompt.trim()
    || isVideoShot;

  const handleGenerate = useCallback(() => {
    if (generateDisabled) return;
    handleTouchUpGenerate();
  }, [generateDisabled, handleTouchUpGenerate]);

  // Grid tap: select touch up target shot; tapping the current target (effectiveTouchUpShot,
  // the id actually highlighted) deselects it.
  const handleGridTap = useCallback((shotId: number) => {
    setTouchUpTargetShotId(shotId === effectiveTouchUpShot?.id ? null : shotId);
  }, [effectiveTouchUpShot, setTouchUpTargetShotId]);

  // Grid preview: long-press / double-click opens fullscreen
  const handleGridPreview = useCallback((shotId: number, cellEl: HTMLElement) => {
    previewSourceEl.current = cellEl;
    setPreviewShotId(shotId);
    setPreviewOpen(true);
  }, []);

  // ImageFlyOverlay items
  const previewItems = useMemo(
    () => allShots.filter(s => s.thumbnailDataUrl).map(s => ({ url: s.thumbnailDataUrl! })),
    [allShots],
  );
  const shotsWithThumbs = useMemo(
    () => allShots.filter(s => s.thumbnailDataUrl),
    [allShots],
  );
  const previewActiveIndex = useMemo(() => {
    if (previewShotId == null) return 0;
    const idx = shotsWithThumbs.findIndex(s => s.id === previewShotId);
    return idx >= 0 ? idx : 0;
  }, [previewShotId, shotsWithThumbs]);

  const handlePreviewIndexChange = useCallback((newIndex: number) => {
    const shot = shotsWithThumbs[newIndex];
    if (shot) setPreviewShotId(shot.id);
  }, [shotsWithThumbs]);

  return (
    <div ref={mainRef} className={styles.main} style={{ gap: 8 }}>
      {/* Video warning. --danger-outline, not --danger-text: this line sits on the NEUTRAL
          workspace surface, not on --danger-bg — the same call the .reviewError block in
          AiWorkspace.module.css documents. Was a raw #e04040 until 20260827. */}
      {isVideoShot && (
        <div className={styles.touchUpInstruction} style={{ color: 'var(--danger-outline)' }}>
          {t('touchUpVideoNotSupported')}
        </div>
      )}

      {/* Controls + Preview row */}
      <div className={styles.controlsRow}>
      {/* Left column: dropdowns + prompt */}
      <div className={styles.controlsLeft}>
      {/* Dropdown row: Model + Touch Up button */}
      <div className={styles.dropdownRow}>
        {/* Model dropdown */}
        <div>
          <div className={styles.sectionLabel}>{t('sectionLabelModel')}</div>
          <DropdownPicker
            selectedLabel={selectedTier}
            selectedSublabel={selectedModel ? `${selectedModel.cost} ${t('creditSuffix')}` : undefined}
            open={modelDropdownOpen}
            onToggle={toggleModelDropdown}
          >
            <div className={styles.tierCards}>
              {models.map((m, i) => {
                const selected = m.id === aiSelectedTouchUpModel;
                const tierLabel = models.length === 1
                  ? t('tierLabelBest')
                  : (i < TIER_LABEL_KEYS.length ? t(TIER_LABEL_KEYS[i]) : m.name);
                const speedPct = Math.round(((maxSeconds - m.estSeconds) / maxSeconds) * 100 + 20);

                return (
                  <button
                    key={m.id}
                    className={`${styles.tierCard} ${selected ? styles.tierCardSelected : ''}`}
                    onClick={() => {
                      setSelectedModel('touchup', m.id);
                      setModelDropdownOpen(false);
                    }}
                  >
                    <div className={styles.tierHeader}>
                      <span className={styles.tierLabel}>{tierLabel}</span>
                      <span className={styles.tierCost}>{m.cost} {t('creditSuffix')}</span>
                    </div>
                    <div className={styles.tierSpeed}>
                      <div className={styles.tierSpeedFill} style={{ width: `${Math.min(100, speedPct)}%` }} />
                    </div>
                    <div className={styles.tierMeta}>
                      <span>{m.name}</span>
                      <span>~{m.estSeconds}s</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </DropdownPicker>
        </div>

        {/* Generate / Auth / Insufficient button + credit line */}
        <div className={styles.generateGroup}>
          {needsAuth && (!capabilities.bridge || standaloneAuthMode) ? (
            <button className={styles.authBtn} onClick={() => { closeWorkspace(); setAuthDialogOpen(true); }}>
              <SparkleIcon />
              {t('signInToEnhance')}
            </button>
          ) : (
            <button
              className={styles.generateBtn}
              onClick={async () => {
                if (interceptEmbeddedSignIn(needsAuth)) return;
                if (insufficient) {
                  const store = useAccountStore.getState();
                  if (!store.welcomeDepletionDismissedThisSession) {
                    const hasPurchased = await store.checkHasEverPurchased();
                    if (!hasPurchased) {
                      closeWorkspace();
                      store.setWelcomeDepletionOpen(true);
                      return;
                    }
                  }
                  closeWorkspace();
                  trackEvent('depletion_shown', { context: 'ai_panel' });
                  setTokenPurchaseDialogOpen(true);
                  return;
                }
                handleGenerate();
              }}
              disabled={generateDisabled}
            >
              <SparkleIcon />
              {t('touchUpEnhance')}
              <span className={styles.creditBadge}>
                ~{perShotCost}cr · {formatTimeEstimate(perShotSeconds)}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Prompt section */}
      <div className={styles.optionsBlock}>
        <div className={styles.sectionLabel} style={{ marginTop: 8 }}>{t('touchUpInstruction')}</div>
        <textarea
          className={styles.promptTextarea}
          value={touchUpPrompt}
          onChange={e => setTouchUpPrompt(e.target.value)}
          placeholder={t('touchUpPromptPlaceholder')}
          rows={3}
          style={{ maxWidth: 600 }}
        />
      </div>
      </div>{/* end controlsLeft */}

      {/* Preview thumbnail — fills remaining space, shrinks to fit */}
      <div className={styles.previewFloat}>
        <WorkspaceShotPreview shot={effectiveTouchUpShot} />
      </div>
      </div>{/* end controlsRow */}

      {/* Revert pill */}
      {canRevert && (
        <div className={styles.revertRow}>
          <button type="button" className={styles.revertPill} onClick={handleRevert}>{t('revert')}</button>
        </div>
      )}

      {/* Divider */}
      <div className={styles.gridDivider} />

      {/* Shot Grid */}
      <ShotGrid
        shots={allShots}
        scenes={allScenes}
        separator={separator}
        selectedIds={selectedIds}
        videoShotIds={videoShotIds}
        approvedShotIds={approvedShotIds}
        onTap={handleGridTap}
        onPreview={handleGridPreview}
        {...(isMobile ? { scrollParent: mainRef.current } : {})}
        /* Land on the selected shot when entering the tab, same as Enhance (owner rule
           2026-09-01, item E). Always handed over: ShotGrid is what withholds the scroll on
           phone/compact layouts, so the rule lives in one place for all four tabs. */
        initialScrollToId={effectiveTouchUpShot?.id ?? null}
      />

      {/* Fullscreen Preview Overlay */}
      <AnimatePresence>
        {previewOpen && previewItems.length > 0 && (
          <ImageFlyOverlay
            key="touchup-preview"
            activeIndex={previewActiveIndex}
            items={previewItems}
            sourceElement={previewSourceEl.current}
            onClose={() => setPreviewOpen(false)}
            onIndexChange={handlePreviewIndexChange}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
